import { useState, forwardRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Edit2, Play, MoreVertical, MessageSquare, ChevronDown, PhoneIncoming, PhoneOutgoing, BarChart3, Trash2, Loader2, AlertTriangle, RotateCw, Lock, Sparkles, Pause, PlayCircle, Copy, Activity, Phone } from 'lucide-react'
import { formatPhoneDisplay } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import api from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/hooks/useAuth'
import { useFeatureGate } from '@/hooks/useFeatureGate'
import { TierBadge } from '@/components/ui/TierBadge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

// Voice names are internal — only used as last-resort fallback on the dashboard, never shown to callers
const voiceNames: Record<string, string> = {}

interface AssistantStats {
  callsThisWeek: number
  avgDurationSeconds: number
  connectedForms?: number
  // ISO timestamp of the most recent call routed to this assistant. Drives
  // the "Last call: 4m ago" line on the card.
  lastCallAt?: string | null
  // Count of calls currently in_progress, ringing, or initiated for this
  // assistant. Drives the green pulsing "Live" pill.
  liveCallCount?: number
}

interface PhoneNumber {
  id: string
  phoneNumber: string
  provider?: string
}

export interface SampleConversationMessage {
  role: 'agent' | 'caller'
  text: string
}

export interface Assistant {
  id: string
  name: string
  firstMessage: string
  voiceId: string
  tonePreset?: string
  assistantType?: 'intake' | 'outbound' | 'receptionist'
  provider?: 'telnyx'
  toolsConfig?: Array<{
    type: string
    enabled: boolean
    config?: {
      destinationNumber?: string
      message?: string
      transferInstructions?: string
      holdMusicUrl?: string
      name?: string
      description?: string
      webhookUrl?: string
      webhookParameters?: Array<{ name: string; type: string; description: string; required: boolean }>
      amdOnTransfer?: { enabled: boolean; action?: 'stop_and_return' | 'leave_message' }
      warmTransfer?: { enabled: boolean; instructions?: string; messageDelayMs?: number }
      multilingual?: boolean
      calendarSource?: 'auto' | 'google' | 'legal_lady'
      consultationLengthMinutes?: number
      bookingInstructions?: string
      bookingPolicy?: 'book_directly' | 'offer_times_only' | 'collect_callback'
      requiredFields?: string[]
      fallbackBehavior?: string
    }
  }>
  systemPrompt?: string
  backgroundSound?: string
  isActive?: boolean
  createdAt: string
  updatedAt: string
  phoneNumbers?: PhoneNumber[]
  stats?: AssistantStats
  sampleConversation?: SampleConversationMessage[]
  voiceDisplayName?: string
  metadata?: {
    kbUploadStatus?: 'pending' | 'success' | 'failed'
    kbUploadedAt?: string
    kbUploadError?: string
  }
}

interface AgentCardProps {
  assistant: Assistant
  dimmed?: boolean
  voiceDisplayName?: string
  onEdit: (assistant: Assistant) => void
  onPreview: (assistant: Assistant) => void
  onViewStats: (assistant: Assistant) => void
  onDelete?: (assistant: Assistant) => void
  onClone?: (assistant: Assistant) => void
  onViewInsights?: (assistant: Assistant) => void
}

// Compact "X ago" formatter for the last-call indicator. Designed to read
// naturally in a small pill: "12s", "4m", "2h", "3d", "Apr 12".
function formatRelativeTime(iso: string | null | undefined): string | null {
  if (!iso) return null
  const then = new Date(iso).getTime()
  if (isNaN(then)) return null
  const seconds = Math.floor((Date.now() - then) / 1000)
  if (seconds < 30) return 'just now'
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .map(w => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

const AgentCard = forwardRef<HTMLDivElement, AgentCardProps>(({ assistant, dimmed, voiceDisplayName, onEdit, onPreview, onViewStats, onDelete, onClone, onViewInsights }, ref) => {
  const [talkTrackOpen, setTalkTrackOpen] = useState(false)
  const [retryingKb, setRetryingKb] = useState(false)
  const [togglingActive, setTogglingActive] = useState(false)
  const { toast } = useToast()
  const { agent: currentUser } = useAuth() as any
  const isSuperAdmin = !!currentUser?.isSuperAdmin
  const featureGate = useFeatureGate()
  const navigate = useNavigate()

  const handleToggleActive = async () => {
    if (togglingActive) return
    const nextActive = assistant.isActive === false
    setTogglingActive(true)
    try {
      await api.post(`/api/voice/assistants/${assistant.id}/active`, { isActive: nextActive })
      toast({
        title: nextActive ? 'Agent resumed' : 'Agent paused',
        description: nextActive
          ? 'Your agent is back online and will handle calls normally.'
          : "Your agent is paused — it won't receive or place calls until you resume it.",
      })
      // Mutate in place so the UI updates without waiting for the parent to
      // refetch (the emitDataChanged from the backend will refetch shortly).
      assistant.isActive = nextActive
    } catch (err: any) {
      toast({
        title: 'Could not update agent',
        description: err?.response?.data?.message || 'Try again in a moment.',
        variant: 'destructive',
      })
    } finally {
      setTogglingActive(false)
    }
  }

  const handleRetryKb = async () => {
    if (retryingKb) return
    setRetryingKb(true)
    try {
      await api.post(`/api/voice/assistants/${assistant.id}/knowledge-base/retry`)
      toast({
        title: 'Retry started',
        description: 'Knowledge base training restarted — this usually takes 1-2 minutes.',
      })
    } catch (err: any) {
      toast({
        title: 'Retry failed',
        description: err?.response?.data?.message || 'Could not restart training. Try again in a moment.',
        variant: 'destructive',
      })
    } finally {
      setRetryingKb(false)
    }
  }

  // AI persona name: prop (from voiceConfig.agentName) → voice map → fallback
  const agentName = voiceDisplayName || voiceNames[assistant.voiceId] || 'AI Assistant'

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }

  const stats = assistant.stats || { callsThisWeek: 0, avgDurationSeconds: 0 }
  const isOutbound = assistant.assistantType === 'outbound'
  const kbStatus = assistant.metadata?.kbUploadStatus
  const isTraining = kbStatus === 'pending'
  const kbFailed = kbStatus === 'failed'
  const liveNow = (stats.liveCallCount || 0) > 0
  const lastCallRelative = formatRelativeTime(stats.lastCallAt)

  // Tier-based lock: outbound needs ai_outbound_agent (Principal), inbound
  // needs ai_inbound_agent (Partner). When the current org's plan doesn't
  // include the feature, we show a frosted overlay on top of the card that
  // keeps the content visible (so users see what they're missing) but
  // disables the actions and foregrounds an upgrade CTA. This is the soft
  // auto-off behavior on downgrade: Principal→Partner locks the outbound
  // card; Partner→Associate locks the inbound card too.
  const requiredFeature: 'ai_outbound_agent' | 'ai_inbound_agent' = isOutbound
    ? 'ai_outbound_agent'
    : 'ai_inbound_agent'
  const isLockedByTier = !featureGate.isAllowed(requiredFeature)
  const lockedTier = isOutbound ? 'Principal' : 'Partner'
  // User-paused agent (distinct from tier lock): shown when the org has
  // access to the feature but the admin has toggled the agent off.
  const isPausedByUser = assistant.isActive === false && !isLockedByTier

  return (
    <Card
      ref={ref}
      className={`group relative overflow-hidden border-border/60 hover:border-brand-royal/40 hover:shadow-xl hover:shadow-brand-royal/5 transition-colors duration-200 ${dimmed ? 'opacity-80' : ''}`}
    >
      {/* Paused overlay — user-controlled "agent is off right now" state.
          Visually softer than the tier-lock pane so it reads as "we paused
          this intentionally" rather than "you need to pay more". */}
      {isPausedByUser && (
        <div className="absolute inset-0 z-20 rounded-xl backdrop-blur-sm bg-background/60 flex items-center justify-center p-4">
          <div className="text-center max-w-xs">
            <div className="mx-auto h-12 w-12 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-3">
              <Pause className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="text-base font-semibold">Agent paused</h3>
            <p className="text-xs text-muted-foreground mt-1 mb-4">
              You paused this agent — it won't take or place calls until you resume it. Your setup is preserved.
            </p>
            <Button
              size="sm"
              onClick={handleToggleActive}
              disabled={togglingActive}
              className="gap-1.5"
            >
              {togglingActive ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <PlayCircle className="h-3.5 w-3.5" />
              )}
              Resume agent
            </Button>
          </div>
        </div>
      )}
      {/* Tier-lock overlay — covers the whole outbound card when the org's
          plan doesn't include ai_outbound_agent. Card content still renders
          underneath (blurred) so users see what they're missing. */}
      {isLockedByTier && (
        <div className="absolute inset-0 z-20 rounded-xl backdrop-blur-sm bg-background/70 flex items-center justify-center p-4">
          <div className="text-center max-w-xs">
            <div className="mx-auto h-12 w-12 rounded-xl bg-gradient-to-br from-brand-royal/10 to-brand-periwinkle/10 border border-brand-royal/20 flex items-center justify-center mb-3">
              <Lock className="h-6 w-6 text-brand-royal" />
            </div>
            <div className="inline-flex items-center gap-2 mb-2">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Locked</span>
              <TierBadge tier={lockedTier.toLowerCase()} size="sm" />
            </div>
            <h3 className="text-base font-semibold">
              {isOutbound ? 'Outbound agent' : 'Inbound agent'}
            </h3>
            <p className="text-xs text-muted-foreground mt-1 mb-4">
              {isOutbound
                ? 'The moment a prospect submits a web inquiry, your AI calls them personally. Upgrade to Principal to activate; your setup stays ready.'
                : "Your AI receptionist answers every inbound call, 24/7. Upgrade to Partner to activate; your setup stays ready."}
            </p>
            <Button
              size="sm"
              onClick={() => navigate('/settings/billing')}
              className="bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:brightness-110 text-white gap-1.5"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Upgrade to {lockedTier}
            </Button>
          </div>
        </div>
      )}
      {/* Top accent line — color-coded by agent type */}
      {dimmed ? (
        <div className="absolute top-0 inset-x-0 h-[3px] border-t-2 border-dashed border-muted-foreground/30 rounded-t-xl" />
      ) : isOutbound ? (
        <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-brand-indigo via-brand-indigo to-brand-royal/60 opacity-80 group-hover:opacity-100 transition-opacity rounded-t-xl" />
      ) : (
        <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-brand-royal/60 via-brand-royal to-brand-indigo opacity-80 group-hover:opacity-100 transition-opacity rounded-t-xl" />
      )}

      <CardContent className="p-6">
        {/* Header: Gradient avatar + name + role */}
        <div className="flex items-start gap-4 mb-5">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-indigo to-brand-royal flex items-center justify-center flex-shrink-0 shadow-sm">
            <span className="text-white font-bold text-sm tracking-wide">
              {getInitials(agentName)}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base leading-snug truncate">{agentName}</h3>
            <div className="flex items-center gap-2 mt-1">
              {isOutbound ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-brand-indigo/10 text-brand-indigo">
                  <PhoneOutgoing className="h-2.5 w-2.5" />
                  Outbound
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-brand-royal/10 text-brand-royal">
                  <PhoneIncoming className="h-2.5 w-2.5" />
                  Inbound
                </span>
              )}
              {liveNow && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        </span>
                        Live
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {(stats.liveCallCount || 0) === 1 ? '1 call in progress on this agent.' : `${stats.liveCallCount} calls in progress on this agent.`}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {!liveNow && lastCallRelative && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                        <Activity className="h-2.5 w-2.5" />
                        {lastCallRelative}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      Last call routed to this agent.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {isTraining && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400">
                        <Loader2 className="h-2.5 w-2.5 animate-spin" />
                        Training
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      Agent is still indexing your website into its knowledge base. This usually takes 1-2 minutes after onboarding — don't hand out the phone number until it's done.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {kbFailed && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/15 transition-colors"
                    >
                      <AlertTriangle className="h-2.5 w-2.5" />
                      KB failed
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    side="bottom"
                    align="start"
                    className="w-[min(22rem,calc(100vw-2rem))] text-xs space-y-2"
                  >
                    <p className="font-semibold text-sm">Website knowledge base didn't finish training</p>
                    <p className="text-muted-foreground">
                      Your agent still answers calls using the firm facts baked into its prompt — it just can't look things up from your website content.
                    </p>
                    {isSuperAdmin && assistant.metadata?.kbUploadError && (
                      <div className="space-y-1">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70 font-semibold">Super-admin diagnostic</p>
                        <div className="rounded bg-muted px-2 py-1.5 text-[11px] font-mono text-muted-foreground break-words max-h-32 overflow-y-auto">
                          {assistant.metadata.kbUploadError}
                        </div>
                      </div>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full h-8 text-xs"
                      onClick={handleRetryKb}
                      disabled={retryingKb}
                    >
                      {retryingKb ? (
                        <>
                          <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                          Restarting…
                        </>
                      ) : (
                        <>
                          <RotateCw className="h-3 w-3 mr-1.5" />
                          Retry training
                        </>
                      )}
                    </Button>
                    <p className="text-[11px] text-muted-foreground">
                      If it keeps failing after a retry, contact support.
                    </p>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>
        </div>

        {/* Mobile phone routing — shown below lg: breakpoint where the
            FlowCanvas SVG connectors aren't visible. Lets clients see which
            number(s) this agent picks up without leaving the agents page. */}
        {assistant.phoneNumbers && assistant.phoneNumbers.length > 0 && (
          <div className="lg:hidden mb-3 flex flex-wrap items-center gap-1.5">
            <Phone className="h-3 w-3 text-muted-foreground" />
            {assistant.phoneNumbers.map(p => (
              <span
                key={p.id}
                className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground"
              >
                {formatPhoneDisplay(p.phoneNumber)}
              </span>
            ))}
          </div>
        )}
        {(!assistant.phoneNumbers || assistant.phoneNumbers.length === 0) && (
          <div className="lg:hidden mb-3">
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border border-dashed border-muted-foreground/30 text-muted-foreground">
              <Phone className="h-2.5 w-2.5" />
              Unassigned
            </span>
          </div>
        )}

        {/* Stats */}
        <div className="rounded-lg bg-muted/50 p-3 mb-4">
          <div className="flex items-center">
            <div className="flex-1 text-center">
              <p className="text-lg font-bold leading-none">{stats.callsThisWeek}</p>
              <p className="text-label text-muted-foreground mt-1.5">Calls</p>
            </div>
            <div className="w-px h-8 bg-border/60" />
            <div className="flex-1 text-center">
              <p className="text-lg font-bold leading-none tabular-nums">{formatDuration(stats.avgDurationSeconds)}</p>
              <p className="text-label text-muted-foreground mt-1.5">Avg Time</p>
            </div>
            {isOutbound && (
              <>
                <div className="w-px h-8 bg-border/60" />
                <div className="flex-1 text-center">
                  <p className="text-lg font-bold leading-none">{stats.connectedForms ?? 0}</p>
                  <p className="text-label text-muted-foreground mt-1.5">Forms</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Sample Conversation */}
        {assistant.sampleConversation && assistant.sampleConversation.length > 0 && (
          <Collapsible open={talkTrackOpen} onOpenChange={setTalkTrackOpen}>
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-2 w-full text-xs text-muted-foreground hover:text-foreground transition-colors mb-4">
                <MessageSquare className="h-3 w-3" />
                <span>Sample Conversation</span>
                <ChevronDown className={`h-3 w-3 ml-auto transition-transform ${talkTrackOpen ? 'rotate-180' : ''}`} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mb-4 space-y-1.5 max-h-44 overflow-y-auto rounded-lg bg-muted/30 p-3">
                {assistant.sampleConversation.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex ${msg.role === 'caller' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-xs ${
                        msg.role === 'agent'
                          ? 'bg-primary/10 text-foreground'
                          : 'bg-muted text-foreground'
                      }`}
                    >
                      <p className="text-label mb-0.5 opacity-50">
                        {msg.role === 'agent' ? agentName : 'Caller'}
                      </p>
                      <p className="leading-relaxed">{msg.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Edit Agent is the primary action on the card — promoted to a
              filled brand-color button with a soft ring + lift on hover so
              it visually leads the eye over the secondary preview/play
              icon next to it. */}
          <Button
            variant="default"
            size="sm"
            className="flex-1 h-9 bg-brand-royal text-white hover:bg-brand-royal/90 ring-1 ring-brand-royal/20 shadow-sm transition-[transform,box-shadow,background-color] duration-200 hover:-translate-y-px hover:shadow-md active:translate-y-0 active:scale-[0.98]"
            onClick={() => onEdit(assistant)}
          >
            <Edit2 className="h-3.5 w-3.5 mr-1.5" />
            Edit Agent
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 text-muted-foreground hover:text-brand-royal disabled:opacity-50"
                    onClick={() => onPreview(assistant)}
                    disabled={isTraining}
                  >
                    {isTraining ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                  </Button>
                </span>
              </TooltipTrigger>
              {isTraining && (
                <TooltipContent>Agent is still training — preview unavailable.</TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onViewStats(assistant)}>
                <BarChart3 className="h-4 w-4 mr-2" />
                View Calls
              </DropdownMenuItem>
              {onViewInsights && (
                <DropdownMenuItem onClick={() => onViewInsights(assistant)}>
                  <Activity className="h-4 w-4 mr-2" />
                  View Insights
                </DropdownMenuItem>
              )}
              {/* Pause / Resume — only meaningful when the tier actually
                  includes the feature. Hidden when the tier-lock overlay is
                  already covering the card (redundant CTA). */}
              {!isLockedByTier && (
                <DropdownMenuItem onClick={handleToggleActive} disabled={togglingActive}>
                  {assistant.isActive === false ? (
                    <>
                      <PlayCircle className="h-4 w-4 mr-2" />
                      Resume agent
                    </>
                  ) : (
                    <>
                      <Pause className="h-4 w-4 mr-2" />
                      Pause agent
                    </>
                  )}
                </DropdownMenuItem>
              )}
              {onClone && (
                <DropdownMenuItem onClick={() => onClone(assistant)}>
                  <Copy className="h-4 w-4 mr-2" />
                  Clone agent
                </DropdownMenuItem>
              )}
              {onDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive" onClick={() => onDelete(assistant)}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Agent
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  )
})

AgentCard.displayName = 'AgentCard'
export default AgentCard
